import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import api from '../api/api.js';
import { promotionSchema } from '../lib/validations';
import styles from './PromotionManagement.module.css';
import Button from '../Components/ui/Button.jsx';
import { useToast } from '../Components/ui/Toast.jsx';
import { useConfirm } from '../Components/ui/ConfirmDialog.jsx';
import f from '../styles/forms.module.css';

const FORM_DEFAULTS = {
  Name:          '',
  Description:   '',
  Code:          '',
  Discount:      '',
  Discount_type: 'percentage',
  Start_date:    '',
  End_date:      '',
  Apply_to:      'all',
};

const PromotionManagement = () => {
  const [promotions, setPromotions]   = useState([]);
  const [routes, setRoutes]           = useState([]);
  const [editingId, setEditingId]     = useState(null);
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [isLoading, setIsLoading]     = useState(false);
  const [showForm, setShowForm]       = useState(false);
  const [serverError, setServerError] = useState('');
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(promotionSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: FORM_DEFAULTS,
  });

  const applyTo      = watch('Apply_to');
  const discountType = watch('Discount_type');

  // ── Data fetching ────────────────────────────────────────────────────────────

  const fetchPromotions = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get('/promotion');
      setPromotions(res.data || []);
    } catch (err) {
      showToast('Error al cargar promociones.', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRoutes = useCallback(async () => {
    try {
      const res = await api.get('/route');
      setRoutes(res.data || []);
    } catch (err) {

    }
  }, []);

  useEffect(() => {
    fetchPromotions();
    fetchRoutes();
  }, [fetchPromotions, fetchRoutes]);

  // ── Form helpers ─────────────────────────────────────────────────────────────

  const resetForm = () => {
    reset(FORM_DEFAULTS);
    setSelectedRoutes([]);
    setEditingId(null);
    setServerError('');
    setShowForm(false);
  };

  const handleRouteToggle = (routeId) => {
    setSelectedRoutes(prev =>
      prev.includes(routeId)
        ? prev.filter(id => id !== routeId)
        : [...prev, routeId]
    );
  };

  // ── Submit ───────────────────────────────────────────────────────────────────

  const onSubmit = async (data) => {
    setServerError('');
    const payload = {
      Name:          data.Name,
      Description:   data.Description || null,
      Code:          data.Code        || null,
      Discount:      Number(data.Discount),
      Discount_type: data.Discount_type,
      Start_date:    data.Start_date,
      End_date:      data.End_date,
      Apply_to:      data.Apply_to,
      routes:        data.Apply_to === 'specific' ? selectedRoutes : [],
    };

    try {
      if (editingId) {
        await api.put(`/promotion/${editingId}`, payload);
        showToast('Promoción actualizada correctamente.', { type: 'success' });
      } else {
        await api.post('/promotion', payload);
        showToast('Promoción creada correctamente.', { type: 'success' });
      }
      resetForm();
      fetchPromotions();
    } catch (err) {
      setServerError(
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.error ||
        'Error al guardar la promoción.'
      );
    }
  };

  // ── Edit ─────────────────────────────────────────────────────────────────────

  const handleEdit = async (promo) => {
    try {
      const res = await api.get(`/promotion/${promo.ID}`);
      const p = res.data;

      reset({
        Name:          p.Name          || '',
        Description:   p.Description   || '',
        Code:          p.Code           || '',
        Discount:      p.Discount != null ? String(p.Discount) : '',
        Discount_type: p.Discount_type  || 'percentage',
        Start_date:    p.Start_date ? p.Start_date.split('T')[0] : '',
        End_date:      p.End_date   ? p.End_date.split('T')[0]   : '',
        Apply_to:      p.Apply_to   || 'all',
      });
      setSelectedRoutes(p.routes ? p.routes.map(r => r.ID) : []);
      setEditingId(p.ID);
      setServerError('');
      setShowForm(true);
    } catch (err) {
      showToast('Error al cargar la promoción.', { type: 'error' });
    }
  };

  // ── Delete / toggle status ───────────────────────────────────────────────────

  const handleDelete = async (id) => {
    const ok = await confirm('¿Deseas deshabilitar esta promoción?', {
      title: 'Confirmar deshabilitación',
      confirmText: 'Deshabilitar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/promotion/${id}`);
      fetchPromotions();
      showToast('Promoción deshabilitada.', { type: 'success' });
    } catch (err) {
      showToast('Error al eliminar la promoción.', { type: 'error' });
    }
  };

  const handleToggleStatus = async (promo) => {
    const newStatus = promo.Status === 'active' ? 'disable' : 'active';
    try {
      await api.put(`/promotion/${promo.ID}`, { Status: newStatus });
      fetchPromotions();
    } catch (err) {
      showToast('Error al cambiar el estado.', { type: 'error' });
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────────

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('es-ES');
  };

  const isCurrentlyActive = (promo) => {
    if (promo.Status !== 'active') return false;
    const now = new Date();
    return now >= new Date(promo.Start_date) && now <= new Date(promo.End_date);
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Gestión de Promociones</h2>
        <Button
          variant={showForm ? 'secondary' : 'primary'}
          onClick={() => { resetForm(); if (!showForm) setShowForm(true); }}
        >
          {showForm ? 'Cancelar' : '+ Nueva Promoción'}
        </Button>
      </div>

      {/* ── Formulario ────────────────────────────────────────────────────────── */}
      {showForm && (
        <div className={f.card} style={{ marginBottom: '24px' }}>
          <div className={f.cardHeader}>
            <div className={f.cardTitleGroup}>
              <h3 className={f.cardTitle}>{editingId ? 'Editar Promoción' : 'Nueva Promoción'}</h3>
              <p className={f.cardDesc}>
                {editingId
                  ? 'Modifica los parámetros de la promoción seleccionada.'
                  : 'Configura los detalles del descuento y el período de vigencia.'}
              </p>
            </div>
            <span className={f.badgeAdmin}>Admin</span>
          </div>
          <hr className={f.divider} />

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            {serverError && <div className={f.serverError} role="alert">{serverError}</div>}

            {/* ── Identificación ── */}
            <div className={f.section}>
              <span className={f.sectionLabel}>Identificación</span>
              <div className={f.grid2}>
                <div className={f.field}>
                  <label htmlFor="promo-name" className={`${f.label} ${f.required}`}>Nombre</label>
                  <input
                    id="promo-name"
                    type="text"
                    {...register('Name')}
                    className={`${f.input} ${errors.Name ? f.inputError : ''}`}
                    aria-describedby={errors.Name ? 'promo-name-error' : undefined}
                    aria-invalid={!!errors.Name}
                    placeholder="Ej: Descuento Navideño"
                  />
                  {errors.Name && <span id="promo-name-error" className={f.fieldError} role="alert">{errors.Name.message}</span>}
                </div>

                <div className={f.field}>
                  <label htmlFor="promo-code" className={f.label}>Código Promocional</label>
                  <input
                    id="promo-code"
                    type="text"
                    {...register('Code')}
                    className={`${f.input} ${errors.Code ? f.inputError : ''}`}
                    aria-describedby={errors.Code ? 'promo-code-error' : 'promo-code-hint'}
                    aria-invalid={!!errors.Code}
                    placeholder="Ej: NAVIDAD2024"
                    style={{ textTransform: 'uppercase' }}
                  />
                  {errors.Code
                    ? <span id="promo-code-error" className={f.fieldError} role="alert">{errors.Code.message}</span>
                    : <span id="promo-code-hint" className={f.fieldHint}>Opcional — si se deja vacío se aplica automáticamente</span>
                  }
                </div>

                <div className={`${f.field} ${f.full}`}>
                  <label htmlFor="promo-desc" className={f.label}>Descripción</label>
                  <input
                    id="promo-desc"
                    type="text"
                    {...register('Description')}
                    className={`${f.input} ${errors.Description ? f.inputError : ''}`}
                    aria-describedby={errors.Description ? 'promo-desc-error' : undefined}
                    aria-invalid={!!errors.Description}
                    placeholder="Descripción breve de la promoción"
                  />
                  {errors.Description && <span id="promo-desc-error" className={f.fieldError} role="alert">{errors.Description.message}</span>}
                </div>
              </div>
            </div>

            {/* ── Descuento ── */}
            <div className={f.section}>
              <span className={f.sectionLabel}>Descuento</span>
              <div className={f.grid2}>
                <div className={f.field}>
                  <label htmlFor="promo-dtype" className={`${f.label} ${f.required}`}>Tipo de Descuento</label>
                  <select id="promo-dtype" className={f.select} {...register('Discount_type')}>
                    <option value="percentage">Porcentaje (%)</option>
                    <option value="fixed">Monto Fijo ($)</option>
                  </select>
                </div>

                <div className={f.field}>
                  <label htmlFor="promo-discount" className={`${f.label} ${f.required}`}>Valor</label>
                  <input
                    id="promo-discount"
                    type="number"
                    {...register('Discount')}
                    className={`${f.input} ${errors.Discount ? f.inputError : ''}`}
                    aria-describedby={errors.Discount ? 'promo-discount-error' : undefined}
                    aria-invalid={!!errors.Discount}
                    min="1"
                    max={discountType === 'percentage' ? '100' : '99999'}
                    placeholder={discountType === 'percentage' ? 'Ej: 20' : 'Ej: 5000'}
                  />
                  {errors.Discount && <span id="promo-discount-error" className={f.fieldError} role="alert">{errors.Discount.message}</span>}
                </div>
              </div>
            </div>

            {/* ── Vigencia ── */}
            <div className={f.section}>
              <span className={f.sectionLabel}>Vigencia</span>
              <div className={f.grid2}>
                <div className={f.field}>
                  <label htmlFor="promo-start" className={`${f.label} ${f.required}`}>Fecha Inicio</label>
                  <input
                    id="promo-start"
                    type="date"
                    {...register('Start_date')}
                    className={`${f.input} ${errors.Start_date ? f.inputError : ''}`}
                    aria-describedby={errors.Start_date ? 'promo-start-error' : undefined}
                    aria-invalid={!!errors.Start_date}
                  />
                  {errors.Start_date && <span id="promo-start-error" className={f.fieldError} role="alert">{errors.Start_date.message}</span>}
                </div>

                <div className={f.field}>
                  <label htmlFor="promo-end" className={`${f.label} ${f.required}`}>Fecha Fin</label>
                  <input
                    id="promo-end"
                    type="date"
                    {...register('End_date')}
                    className={`${f.input} ${errors.End_date ? f.inputError : ''}`}
                    aria-describedby={errors.End_date ? 'promo-end-error' : undefined}
                    aria-invalid={!!errors.End_date}
                  />
                  {errors.End_date && <span id="promo-end-error" className={f.fieldError} role="alert">{errors.End_date.message}</span>}
                </div>
              </div>
            </div>

            {/* ── Aplicación ── */}
            <div className={f.section}>
              <span className={f.sectionLabel}>Aplicación</span>
              <div className={f.field} style={{ maxWidth: '260px', marginBottom: '12px' }}>
                <label htmlFor="promo-applyto" className={`${f.label} ${f.required}`}>Aplicar a</label>
                <select id="promo-applyto" className={f.select} {...register('Apply_to')}>
                  <option value="all">Todas las rutas</option>
                  <option value="specific">Rutas específicas</option>
                </select>
              </div>

              {applyTo === 'specific' && (
                <div className={f.field}>
                  <span className={f.label}>Seleccionar Rutas</span>
                  <div className={styles.routesGrid}>
                    {routes.map(route => (
                      <label key={route.ID} className={styles.routeCheckbox}>
                        <input
                          type="checkbox"
                          checked={selectedRoutes.includes(route.ID)}
                          onChange={() => handleRouteToggle(route.ID)}
                        />
                        <span>{route.Origin} → {route.Destination}</span>
                      </label>
                    ))}
                  </div>
                  {routes.length === 0 && <p className={styles.emptyMessage}>No hay rutas disponibles</p>}
                </div>
              )}
            </div>

            {/* ── Acciones ── */}
            <div className={f.actions}>
              <Button type="submit" variant="primary" loading={isSubmitting}>
                {editingId ? 'Actualizar Promoción' : 'Crear Promoción'}
              </Button>
              <Button type="button" variant="secondary" onClick={resetForm}>
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* ── Lista de Promociones ──────────────────────────────────────────────── */}
      {isLoading ? (
        <div className={styles.loadingMessage}>Cargando promociones...</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Código</th>
                <th>Descuento</th>
                <th>Vigencia</th>
                <th>Aplica a</th>
                <th>Estado</th>
                <th className={styles.actionsHeader}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {promotions.length === 0 ? (
                <tr>
                  <td colSpan="7" className={styles.emptyMessage}>
                    No hay promociones registradas
                  </td>
                </tr>
              ) : (
                promotions.map(promo => (
                  <tr key={promo.ID}>
                    <td>
                      <strong>{promo.Name}</strong>
                      {promo.Description && (
                        <div className={styles.description}>{promo.Description}</div>
                      )}
                    </td>
                    <td>
                      {promo.Code ? (
                        <span className={styles.codeTag}>{promo.Code}</span>
                      ) : (
                        <span className={styles.autoTag}>Automático</span>
                      )}
                    </td>
                    <td>
                      <span className={styles.discountBadge}>
                        {promo.Discount_type === 'percentage'
                          ? `${promo.Discount}%`
                          : `$${promo.Discount}`}
                      </span>
                    </td>
                    <td>
                      {formatDate(promo.Start_date)} - {formatDate(promo.End_date)}
                    </td>
                    <td>
                      {promo.Apply_to === 'all'
                        ? 'Todas las rutas'
                        : <span>{promo.routes_count || 0} rutas</span>
                      }
                    </td>
                    <td>
                      {isCurrentlyActive(promo) ? (
                        <span className={styles.statusActive}>Activa</span>
                      ) : promo.Status === 'active' ? (
                        <span className={styles.statusPending}>Programada</span>
                      ) : (
                        <span className={styles.statusInactive}>Inactiva</span>
                      )}
                    </td>
                    <td className={styles.actionsCell}>
                      <button
                        className={styles.btnEdit}
                        onClick={() => handleEdit(promo)}
                        title="Editar"
                      >
                        Editar
                      </button>
                      <button
                        className={promo.Status === 'active' ? styles.btnDisable : styles.btnEnable}
                        onClick={() => handleToggleStatus(promo)}
                        title={promo.Status === 'active' ? 'Desactivar' : 'Activar'}
                      >
                        {promo.Status === 'active' ? 'Desactivar' : 'Activar'}
                      </button>
                      {promo.Status !== 'active' && (
                        <button
                          className={styles.btnDelete}
                          onClick={() => handleDelete(promo.ID)}
                          title="Eliminar"
                        >
                          Eliminar
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PromotionManagement;
